import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiMapPin,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { eventAPI } from "../services/api";
import { formatDate, resolveImageUrl } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";

const CATEGORIES = [
  "All",
  "Academic",
  "Sports",
  "Cultural",
  "Meeting",
  "Holiday",
  "Other",
];

const categoryColors = {
  Academic: "bg-blue-100 text-blue-700",
  Sports: "bg-green-100 text-green-700",
  Cultural: "bg-pink-100 text-pink-700",
  Meeting: "bg-purple-100 text-purple-700",
  Holiday: "bg-orange-100 text-orange-700",
  Other: "bg-gray-100 text-gray-700",
};

const NewsEvents = () => {
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState("All");
  const [page, setPage] = useState(1);
  const perPage = 6;

  const { data: events = [], loading } = useDataRefresh(
    async () => {
      const response = await eventAPI.getAll();
      return response?.data?.data?.events || [];
    },
    "events",
    { pollInterval: 10000 }, // Refresh every 10 seconds
  );

  useEffect(() => {
    const result =
      category === "All"
        ? events
        : events.filter((e) => e.category === category);
    setFiltered(result);
    setPage(1);
  }, [category, events]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Stay Informed
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            News & Events
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Stay updated with the latest happenings at Gyanpunja English
            Boarding Secondary School
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <FiFilter className="text-gray-400 flex-shrink-0" />
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat
                  ? "bg-primary-500 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginated.map((event, idx) => (
                <motion.article
                  key={event._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm card-hover"
                >
                  <div className="h-40 bg-gradient-to-br from-primary-500 to-primary-700 relative flex items-center justify-center overflow-hidden">
                    {event.image?.url ? (
                      <img
                        src={resolveImageUrl(event.image.url)}
                        alt={event.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <FiCalendar className="w-12 h-12 text-white/20" />
                    )}
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="absolute top-4 left-4 z-10">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[event.category] || categoryColors.Other}`}
                      >
                        {event.category || "Event"}
                      </span>
                    </div>
                    {event.date && (
                      <div className="absolute bottom-4 right-4 z-10 bg-white/90 rounded-lg px-3 py-1.5 text-center">
                        <div className="text-primary-500 font-bold text-sm">
                          {formatDate(event.date, "MMM dd")}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-primary-500 text-lg mb-2 font-serif">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {event.description}
                    </p>
                    <div className="space-y-1.5">
                      {event.date && (
                        <div className="flex items-center gap-2 text-gray-400 text-xs">
                          <FiCalendar className="w-3 h-3" />
                          {formatDate(event.date)}
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-2 text-gray-400 text-xs">
                          <FiMapPin className="w-3 h-3" />
                          {event.location}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16">
                <FiCalendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">
                  No events found for this category
                </p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-12">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-200 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-lg font-medium text-sm transition-colors ${
                      page === i + 1
                        ? "bg-primary-500 text-white"
                        : "border border-gray-200 hover:border-primary-400 text-gray-700"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-200 hover:border-primary-400 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewsEvents;
